import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toaster';
import { normalizeError } from '@/lib/api';
import { useSubmitPlanningWorklog } from '@/hooks/use-planning';
import type { PlanningItem } from '@/features/planning/types';

interface PlanningWorklogFormProps {
  item: PlanningItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PlanningWorklogForm({ item, open, onOpenChange }: PlanningWorklogFormProps) {
  const submitWorklog = useSubmitPlanningWorklog();
  const [mailsProcessed, setMailsProcessed] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMailsProcessed(item.workLog ? String(item.workLog.mailsProcessed) : '');
    setNote(item.workLog?.note ?? '');
    setFormError(null);
  }, [item, open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    if (mailsProcessed.trim() === '') {
      setFormError('Mails processed is required.');
      return;
    }

    const parsed = Number(mailsProcessed);
    if (!Number.isInteger(parsed) || parsed < 0) {
      setFormError('Mails processed must be a non-negative integer.');
      return;
    }

    try {
      await submitWorklog.mutateAsync({
        planningItemId: item.id,
        weekId: item.weekId,
        mailsProcessed: parsed,
        note: note.trim() ? note : undefined,
      });
      toast({ title: item.workLog ? 'Worklog updated' : 'Worklog submitted' });
      onOpenChange(false);
    } catch (error: unknown) {
      setFormError(normalizeError(error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{item.workLog ? 'Edit worklog' : 'Submit worklog'}</DialogTitle>
          <DialogDescription>
            Enter processed mails for this planning item.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mails-processed-input">Mails processed</Label>
            <Input
              id="mails-processed-input"
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              value={mailsProcessed}
              onChange={(event) => setMailsProcessed(event.target.value)}
              placeholder="0"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="worklog-note-input">Note (optional)</Label>
            <Textarea
              id="worklog-note-input"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Add context for this worklog..."
              rows={4}
            />
          </div>

          {formError && <p className="text-sm text-destructive">{formError}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitWorklog.isPending}>
              {submitWorklog.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitWorklog.isPending
                ? item.workLog
                  ? 'Updating...'
                  : 'Submitting...'
                : item.workLog
                  ? 'Update worklog'
                  : 'Submit worklog'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
